import type { UsersReportFilters, UsersReportRow } from '@/services';
import { useDataTablePageSync } from '@/shared/hooks';
import { SearchFilterField } from '@/shared/ui';
import { toaster } from '@/shared/ui/toaster.component';
import { f, useFilters } from '@/shared/utils';
import { formatDateTimeToBrazil } from '@/shared/utils/format-date.util';
import { UserGroup, getScopeFromGroupName } from '@/shared/utils/get-group';
import { getRoleLabel } from '@/shared/utils/get-role-label';
import { Button, DataTable } from '@noorden-insurtech/alba-design-system';
import { Download, Funnel, FunnelX, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useUsersReport, useUsersReportExport } from '../../hooks';
import {
  USERS_REPORT_SORT_FIELD_BY_COLUMN,
  USERS_REPORT_TABLE_CLASS,
  usersReportColumns,
} from './users-report-columns';
import {
  UsersReportFilters as UsersReportFiltersPanel,
  type UsersReportFilterValues,
} from './users-report-filters.component';
import { UsersReportSummary } from './users-report-summary.component';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const STATUS_OPTIONS = [
  { label: 'Primeiro acesso', value: 'FORCE_CHANGE_PASSWORD' },
  { label: 'Ativo', value: 'ACTIVE' },
  { label: 'Inativo', value: 'INACTIVE' },
];

const INVITE_OPTIONS = [
  { label: 'Convite expirado', value: 'true' },
  { label: 'Convite vigente', value: 'false' },
];

const ROLE_OPTIONS = Object.values(UserGroup)
  .map(groupName => ({
    label: `${getScopeFromGroupName(groupName)} - ${getRoleLabel(groupName)}`,
    value: groupName,
  }))
  .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

const COLUMN_BY_SORT_FIELD = Object.fromEntries(
  Object.entries(USERS_REPORT_SORT_FIELD_BY_COLUMN).map(([column, field]) => [
    field,
    column,
  ])
);

const EMPTY_PANEL_FILTERS: UsersReportFilterValues = {
  status: '',
  groupName: '',
  brokerCode: '',
  advisoryCode: '',
  productCode: '',
  inviteExpired: '',
  invitedFrom: '',
  invitedTo: '',
  lastLoginFrom: '',
  lastLoginTo: '',
};

const toItems = (list?: { code: string; name: string }[]) =>
  (list ?? []).map(option => ({ label: option.name, value: option.code }));

/**
 * Relatório de usuários da plataforma (uso interno da seguradora): quem ainda
 * não fez o primeiro acesso, validade do convite, último login e produtos
 * ativos por corretora, com exportação para Excel.
 */
export function UsersReportPage() {
  const [showFilters, setShowFilters] = useState(false);
  const { filters, debouncedFilters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    status: f.string().withDefault(''),
    groupName: f.string().withDefault(''),
    brokerCode: f.string().withDefault(''),
    advisoryCode: f.string().withDefault(''),
    productCode: f.string().withDefault(''),
    inviteExpired: f.string().withDefault(''),
    invitedFrom: f.string().withDefault(''),
    invitedTo: f.string().withDefault(''),
    lastLoginFrom: f.string().withDefault(''),
    lastLoginTo: f.string().withDefault(''),
    sortField: f.string().withDefault('name'),
    sortType: f.string().withDefault('asc'),
    page: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(20),
  });

  const reportFilters: UsersReportFilters = useMemo(
    () => ({
      search: debouncedFilters.search,
      statuses: debouncedFilters.status,
      groupName: debouncedFilters.groupName,
      brokerCode: debouncedFilters.brokerCode,
      advisoryCode: debouncedFilters.advisoryCode,
      productCode: debouncedFilters.productCode,
      inviteExpired: debouncedFilters.inviteExpired,
      invitedFrom: debouncedFilters.invitedFrom,
      invitedTo: debouncedFilters.invitedTo,
      lastLoginFrom: debouncedFilters.lastLoginFrom,
      lastLoginTo: debouncedFilters.lastLoginTo,
      sortField: debouncedFilters.sortField,
      sortType: debouncedFilters.sortType === 'desc' ? 'desc' : 'asc',
    }),
    [debouncedFilters]
  );

  const { data, isLoading, isFetching, error, refetch } = useUsersReport({
    ...reportFilters,
    page: debouncedFilters.page,
    pageSize: debouncedFilters.pageSize,
  });
  const exportMutation = useUsersReportExport();
  const dataTableRef = useDataTablePageSync<UsersReportRow>(filters.page);

  const options = data?.filterOptions;
  // Cascata: com uma assessoria selecionada, só as corretoras vinculadas a ela.
  const brokerOptions = useMemo(
    () =>
      toItems(
        filters.advisoryCode
          ? options?.brokers.filter(
              broker => broker.advisoryCode === filters.advisoryCode
            )
          : options?.brokers
      ),
    [options?.brokers, filters.advisoryCode]
  );
  const advisoryOptions = useMemo(
    () => toItems(options?.advisories),
    [options?.advisories]
  );
  const productOptions = useMemo(
    () => toItems(options?.products),
    [options?.products]
  );

  const sorting = useMemo(() => {
    const columnId = COLUMN_BY_SORT_FIELD[filters.sortField];
    return columnId
      ? [{ id: columnId, desc: filters.sortType === 'desc' }]
      : [];
  }, [filters.sortField, filters.sortType]);

  const panelValues: UsersReportFilterValues = {
    status: filters.status,
    groupName: filters.groupName,
    brokerCode: filters.brokerCode,
    advisoryCode: filters.advisoryCode,
    productCode: filters.productCode,
    inviteExpired: filters.inviteExpired,
    invitedFrom: filters.invitedFrom,
    invitedTo: filters.invitedTo,
    lastLoginFrom: filters.lastLoginFrom,
    lastLoginTo: filters.lastLoginTo,
  };
  const activeFilterCount = [
    filters.status,
    filters.groupName,
    filters.brokerCode,
    filters.advisoryCode,
    filters.productCode,
    filters.inviteExpired,
    filters.invitedFrom,
    filters.lastLoginFrom,
  ].filter(Boolean).length;

  const handlePanelChange = (patch: Partial<UsersReportFilterValues>) => {
    const next = { ...filters, ...patch, page: 1 };
    // Ao trocar a assessoria, descarta a corretora que não pertence a ela.
    if (patch.advisoryCode && next.brokerCode) {
      const selectedBroker = options?.brokers.find(
        broker => broker.code === next.brokerCode
      );
      if (selectedBroker?.advisoryCode !== patch.advisoryCode) {
        next.brokerCode = '';
      }
    }
    setFilters(next);
  };

  const handleExport = () =>
    exportMutation.mutate(reportFilters, {
      onError: exportError =>
        toaster.create({
          type: 'error',
          title: 'Erro ao exportar',
          description: exportError.message,
        }),
    });

  const rows = data?.data ?? [];

  return (
    <div className='h-full space-y-6 overflow-auto pr-1'>
      <div className='flex flex-col gap-1'>
        <h2 className='font-gotham text-xl font-bold text-alba-black-500'>
          Usuários da plataforma
        </h2>
        <p className='text-sm text-alba-gray-600'>
          Status atual de cada usuário, validade do convite de primeiro acesso,
          último login e produtos ativos da corretora.
          {data?.generatedAt && (
            <> Dados de {formatDateTimeToBrazil(data.generatedAt)}.</>
          )}
        </p>
      </div>

      <UsersReportSummary summary={data?.summary} loading={isLoading} />

      <div className='flex flex-col'>
        {/* Barra: busca + botão Filtros (esquerda) | Atualizar + Exportar (direita). */}
        <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
          <div className='flex flex-1 items-center gap-3'>
            <div className='max-w-xl flex-1'>
              <SearchFilterField
                {...registerFilter('search')}
                placeholder='Buscar por nome, e-mail, CPF ou corretora...'
                className='w-full'
              />
            </div>
            <button
              type='button'
              onClick={() => setShowFilters(value => !value)}
              aria-expanded={showFilters}
              className='flex h-10 shrink-0 items-center gap-2 rounded-lg border border-alba-gray-400 px-4 text-sm text-alba-gray-600 transition-colors hover:bg-alba-gray-100'
            >
              {showFilters ? (
                <FunnelX className='h-4 w-4' strokeWidth={1.8} />
              ) : (
                <Funnel className='h-4 w-4' strokeWidth={1.8} />
              )}
              Filtros
              {activeFilterCount > 0 && (
                <span className='flex h-5 min-w-5 items-center justify-center rounded-full bg-alba-blue-500 px-1 text-xs font-bold text-white'>
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label='Atualizar dados'
              title='Atualizar dados'
              className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-alba-gray-400 text-alba-gray-600 transition-colors hover:bg-alba-gray-100 disabled:opacity-50'
            >
              <RefreshCw
                className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
                strokeWidth={1.8}
              />
            </button>
            <Button
              variant='primary'
              size='md'
              onClick={handleExport}
              disabled={!rows.length || exportMutation.isPending}
            >
              <Download className='mr-2 h-4 w-4' />
              {exportMutation.isPending ? 'Exportando...' : 'Exportar Excel'}
            </Button>
          </div>
        </div>

        <UsersReportFiltersPanel
          open={showFilters}
          hasActiveFilter={activeFilterCount > 0}
          values={panelValues}
          statusOptions={STATUS_OPTIONS}
          roleOptions={ROLE_OPTIONS}
          brokerOptions={brokerOptions}
          advisoryOptions={advisoryOptions}
          productOptions={productOptions}
          inviteOptions={INVITE_OPTIONS}
          onChange={handlePanelChange}
          onClear={() =>
            setFilters({ ...filters, ...EMPTY_PANEL_FILTERS, page: 1 })
          }
        />
      </div>

      {error ? (
        <div className='py-8 text-center text-alba-error-red-500'>
          Erro ao carregar o relatório de usuários. Tente novamente.
        </div>
      ) : (
        <div
          className={`min-w-0 overflow-x-auto rounded-3xl ${USERS_REPORT_TABLE_CLASS}`}
        >
          <DataTable
            ref={dataTableRef}
            className='w-full'
            loading={isLoading}
            columns={usersReportColumns}
            data={rows}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            totalItems={data?.meta.total ?? 0}
            totalPages={data?.meta.totalPages ?? 0}
            pageSize={filters.pageSize}
            manualSorting
            sorting={sorting}
            onSortChange={nextSorting => {
              const nextSort = nextSorting[0];
              setFilters({
                ...filters,
                page: 1,
                sortField: nextSort
                  ? (USERS_REPORT_SORT_FIELD_BY_COLUMN[nextSort.id] ?? 'name')
                  : 'name',
                sortType: nextSort?.desc ? 'desc' : 'asc',
              });
            }}
            onPageChange={(page, pageSize) =>
              setFilters({ ...filters, page, pageSize })
            }
          />
        </div>
      )}
    </div>
  );
}
